// 인증 관련 유틸리티 함수

/**
 * Basic 토큰 생성 (email:password를 base64 인코딩)
 */
export const createBasicToken = (email: string, password: string): string => {
  const credentials = `${email}:${password}`;
  return btoa(credentials);
};

/**
 * 쿠키에서 특정 쿠키 값을 가져오는 함수
 */
export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift() || null;
    return cookieValue;
  }
  return null;
};

/**
 * 모든 쿠키를 로깅하는 디버깅 함수
 */
export const logAllCookies = (): void => {
  console.log('🍪 현재 모든 쿠키:', {
    allCookies: document.cookie,
    parsedCookies: document.cookie.split(';').map((c) => {
      const [key, value] = c.trim().split('=');
      return { key, value: value?.substring(0, 20) + '...' };
    }),
  });
};

/**
 * 쿠키에서 accessToken을 가져오는 함수
 */
export const getAccessToken = (): string | null => {
  return getCookie('accessToken');
};

/**
 * 쿠키에서 refreshToken을 가져오는 함수
 * 쿠키에서 읽을 수 없으면 sessionStorage에서 읽기 (백엔드가 응답 body에 포함한 경우)
 */
export const getRefreshToken = (): string | null => {
  // 먼저 쿠키에서 시도
  const cookieToken = getCookie('refreshToken');
  if (cookieToken) {
    return cookieToken;
  }

  // 쿠키에서 읽을 수 없으면 sessionStorage에서 읽기 (로그인 시 저장된 경우)
  const storedToken = sessionStorage.getItem('refreshToken');
  if (storedToken) {
    return storedToken;
  }

  return null;
};

/**
 * refreshToken을 sessionStorage에 저장하는 함수
 * 백엔드가 로그인 응답 body에 refreshToken을 포함한 경우 사용
 */
export const setRefreshToken = (token: string): void => {
  sessionStorage.setItem('refreshToken', token);
};

/**
 * refreshToken을 sessionStorage에서 제거하는 함수
 */
export const removeRefreshToken = (): void => {
  sessionStorage.removeItem('refreshToken');
};

/**
 * 권한 등급 관련 유틸리티 함수
 */

import { ROLE_PRIORITY } from '@repo/types';
import { ACCOUNT_KIND } from '@theailab-dev/parchment';
import type { AccountKind } from '@theailab-dev/parchment';

/**
 * 권한 등급 레벨 반환 (@repo/types ROLE_PRIORITY와 동일)
 */
export const getAccountKindLevel = (kind: AccountKind): number => {
  return ROLE_PRIORITY[kind] ?? 0;
};

/**
 * 현재 사용자 권한이 대상 권한보다 높거나 같은지 확인
 *
 * @param currentUserKind - 현재 로그인한 사용자의 권한
 * @param targetKind - 비교 대상 권한
 * @returns 현재 사용자 권한이 대상 권한보다 높거나 같으면 true
 */
export const isHigherOrEqual = (currentUserKind: AccountKind, targetKind: AccountKind): boolean => {
  const currentLevel = getAccountKindLevel(currentUserKind);
  const targetLevel = getAccountKindLevel(targetKind);
  return currentLevel >= targetLevel;
};

/**
 * 현재 사용자가 대상 계정을 수정할 수 있는지 확인
 *
 * 정책:
 * - OFFICER는 읽기만 가능 (변경 불가)
 * - 상세 규칙은 ROLE_PRIORITY 기준 (본인보다 낮은 등급만 수정)
 *
 * @param currentUserKind - 현재 로그인한 사용자의 권한
 * @param targetAccountKind - 수정하려는 대상 계정의 권한
 * @returns 수정 가능하면 true, 불가능하면 false
 */
export const canModifyAccount = (currentUserKind: AccountKind, targetAccountKind: AccountKind): boolean => {
  if (currentUserKind === ACCOUNT_KIND.OFFICER) {
    return false;
  }

  // 역할 우선순위가 정의되지 않은 경우 안전하게 false 반환
  if (ROLE_PRIORITY[currentUserKind] === undefined || ROLE_PRIORITY[targetAccountKind] === undefined) {
    return false;
  }

  // 내 권한이 대상 권한보다 높아야 수정 가능 (같거나 낮으면 불가)
  return ROLE_PRIORITY[currentUserKind] > ROLE_PRIORITY[targetAccountKind];
};

/**
 * 현재 사용자가 특정 권한으로 변경할 수 있는지 확인
 *
 * 정책:
 * - OFFICER는 어떤 권한으로도 변경 불가
 * - ADMIN 유형으로는 UI에서 변경 불가 (별도 정책)
 * - 그 외는 ROLE_PRIORITY 기준 >= 이면 허용 (백엔드 canChangeToRole과 동일)
 *
 * @param currentUserKind - 현재 로그인한 사용자의 권한
 * @param targetKind - 변경하려는 대상 권한
 * @returns 변경 가능하면 true, 불가능하면 false
 */
export const canChangeToKind = (currentUserKind: AccountKind, targetKind: AccountKind): boolean => {
  if (currentUserKind === ACCOUNT_KIND.OFFICER) {
    return false;
  }

  // SYSTEM으로는 변경 불가 (최고 권한은 시스템에서만 부여 가능하거나 유일함)
  if (targetKind === ACCOUNT_KIND.ADMIN) {
    return false;
  }

  // 역할 우선순위가 정의되지 않은 경우 안전하게 false 반환
  if (ROLE_PRIORITY[currentUserKind] === undefined || ROLE_PRIORITY[targetKind] === undefined) {
    return false;
  }

  // 내 권한이 대상 권한보다 높거나 같아야 변경 가능 (>= 비교)
  // 백엔드 canChangeToRole과 동일한 로직
  return ROLE_PRIORITY[currentUserKind] >= ROLE_PRIORITY[targetKind];
};
